import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { adminAPI } from "../services/admin.api";
import type { Content } from "../types";
import { useLanguage } from "../../../context/LanguageContext";

export const useContentsManagement = () => {
  const { t } = useLanguage();
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    type: "file" | "external";
    src: string;
  } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getContents();
      setContents(res.data ?? res ?? []);
    } catch {
      toast.error(t("toast.content.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      !searchTerm
        ? contents
        : contents.filter(
            (c) =>
              c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (typeof c.category === "string"
                ? c.category
                : ((c.category as any)?.name ?? "")
              )
                .toLowerCase()
                .includes(searchTerm.toLowerCase()),
          ),
    [contents, searchTerm],
  );

  const openEdit = (content: Content) => {
    setEditingContent({ ...content });
    setShowEditModal(true);
  };
  const closeEdit = () => {
    setShowEditModal(false);
    setEditingContent(null);
  };

  const save = async () => {
    if (!editingContent) return;
    if (!editingContent.title || !editingContent.category) {
      toast.error(t("toast.content.titleCategoryRequired"));
      return;
    }
    setSaving(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const videoFile = editingContent.videoFile;

      if (editingContent.id) {
        const formData = new FormData();
        formData.append("title", editingContent.title);
        formData.append(
          "category",
          typeof editingContent.category === "string"
            ? editingContent.category
            : ((editingContent.category as any)?.name ?? ""),
        );
        formData.append("description", editingContent.description ?? "");
        if (videoFile instanceof File) {
          formData.append("video", videoFile);
        } else if (editingContent.video_url) {
          formData.append("video_url", editingContent.video_url);
        }
        if (editingContent.metrics) {
          formData.append("metrics", JSON.stringify(editingContent.metrics));
        }
        await adminAPI.updateContent(String(editingContent.id), formData, (pct) => setUploadProgress(pct));
      } else {
        const createData = new FormData();
        createData.append("title", editingContent.title);
        createData.append(
          "category",
          typeof editingContent.category === "string"
            ? editingContent.category
            : ((editingContent.category as any)?.name ?? ""),
        );
        createData.append("description", editingContent.description ?? "");
        if (videoFile instanceof File) {
          createData.append("video", videoFile);
        } else if (editingContent.video_url) {
          createData.append("video_url", editingContent.video_url);
        }
        if (editingContent.metrics) {
          createData.append("metrics", JSON.stringify(editingContent.metrics));
        }
        await adminAPI.createContent(createData, (pct) => setUploadProgress(pct));
      }

      toast.success(t("toast.content.saved"));
      closeEdit();
      await load();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("toast.content.saveFailed");
      setUploadError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: number) => {
    try {
      await adminAPI.deleteContent(String(id));
      toast.success(t("toast.content.deleted"));
      await load();
    } catch {
      toast.error(t("toast.content.deleteFailed"));
    }
  };
  return {
    loading,
    saving,
    uploadProgress,
    uploadError,
    filtered,
    searchTerm,
    setSearchTerm,
    editingContent,
    setEditingContent,
    showEditModal,
    openEdit,
    closeEdit,
    save,
    selectedVideo,
    setSelectedVideo,
    remove,
  };
};
